import { useTheme } from '@emotion/react';
import { useRef, useState, useCallback, useEffect } from 'react';
import { usePrevious } from 'react-use';
import { Flex } from '../Flex/index.js';
import { ScreenReaderOnly } from '../ScreenReaderOnly/index.js';
import { Text } from '../Text/index.js';
import StepIndicator from './StepIndicator.js';
import { List, StepStatus, StepBody, Metadata } from './styles.js';
import { TimelineStepStatus } from './types.js';
import animationStepStatus from './utils/animationStepStatus.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';

const STEP_ANIMATION_DELTA = 450;
function getStatus(step, status, animationStep, currentStepNumber, stepsCount, translate) {
  const currentAnimationStatus = animationStepStatus(step, status, animationStep, currentStepNumber, stepsCount);
  const localizations = {
    [TimelineStepStatus.Alert]: translate('Timeline.alert'),
    [TimelineStepStatus.InProgress]: translate('Timeline.inProgress'),
    [TimelineStepStatus.Success]: translate('Timeline.success')
  };
  return localizations[currentAnimationStatus];
}
const Timeline = ({
  animate = true,
  animateFromStep = 0,
  steps,
  ...rest
}) => {
  const currentStepIndex = steps.findIndex(step => step.isCurrentStep);
  const currentStepNumber = currentStepIndex === -1 ? 0 : currentStepIndex + 1;
  const {
    translate
  } = useTheme();
  const requestAnimationFrameRef = useRef({
    id: 0,
    timestamp: 0
  });
  const previousCurrentStep = usePrevious(currentStepNumber);
  const [animating, setAnimating] = useState(animate && currentStepNumber !== 0);
  const [animationStep, setAnimationStep] = useState(animate ? animateFromStep : currentStepNumber);
  const startAnimation = useCallback(() => {
    function playAnimation(timestamp) {
      if (!requestAnimationFrameRef.current.timestamp) {
        requestAnimationFrameRef.current.timestamp = timestamp;
      }
      const interval = timestamp - requestAnimationFrameRef.current.timestamp;
      if (interval > STEP_ANIMATION_DELTA) {
        requestAnimationFrameRef.current.timestamp = timestamp;
        if (animationStep < currentStepNumber) {
          setAnimationStep(animationStep + 1);
        } else if (animationStep > currentStepNumber) {
          setAnimationStep(animationStep - 1);
        } else {
          setAnimating(false);
        }
      } else if (animating) {
        requestAnimationFrameRef.current.id = requestAnimationFrame(playAnimation);
      }
    }
    requestAnimationFrameRef.current.id = requestAnimationFrame(playAnimation);
  }, [animationStep, currentStepNumber, animating]);
  useEffect(() => {
    const currentAnimationRef = requestAnimationFrameRef.current;
    if (animating) {
      startAnimation();
    } else {
      cancelAnimationFrame(currentAnimationRef.id);
    }
    return () => cancelAnimationFrame(currentAnimationRef.id);
  }, [animating, startAnimation]);
  useEffect(() => {
    if (animate && !animating && previousCurrentStep !== currentStepNumber) {
      setAnimating(true);
    }
  }, [animating, animate, previousCurrentStep, currentStepNumber]);
  return jsx(List, {
    ...rest,
    children: steps.map((step, index) => {
      const StepLabel = typeof step.label === 'string' ? jsx(Text.Body, {
        bold: true,
        children: step.label
      }) : step.label;
      return jsx("li", {
        "data-testid": `timeline-step-${index + 1}${animationStep === index + 1 ? '-current' : ''}`,
        children: jsxs(Flex, {
          children: [jsx(StepStatus, {
            flexDirection: "column",
            alignItems: "center",
            children: jsx(StepIndicator, {
              step: index + 1,
              status: step.status,
              stepsCount: steps.length,
              animationStep: animationStep,
              currentStepNumber: currentStepNumber
            })
          }), jsxs(StepBody, {
            children: [jsx(ScreenReaderOnly, {
              children: getStatus(index + 1, step.status, animationStep, currentStepNumber, steps.length, translate)
            }), StepLabel, jsx(Text.Body, {
              size: "sm",
              children: step.sublabel
            }), step.metadata ? jsx(Metadata, {
              size: "xs",
              children: step.metadata
            }) : null]
          })]
        })
      }, step.key !== undefined ? step.key : `timeline-step-${step.label}`);
    })
  });
};

export { Timeline };
